import * as React from 'react';
import { ExpandableDatePickerProps, CalendarViewType } from '../schema';
import { formatMonthDayYear, addDays, addMonths, formatMonthName, asDate } from 'owa-datetime';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';
import { observableToday } from 'owa-observable-datetime';
import { createDateRangeFromType, DateRange, createWorkWeekDaysArray } from 'owa-datetime-utils';
import { getFluentDateRangeType, isDayGridViewType } from '../utils/calendarViewTypeUtils';
import {
    getFirstDayOfWeek,
    getWorkDays,
    formatDateForStrip,
    getShowWeekNumbers,
    getFirstWeekOfYear,
    convertToOwaDate,
} from '../utils/dateUtils';
import { TodayButtonTextHideDay } from '../constants';
import {
    goToTodayButtonTooltip,
    goToNextDateRange,
    goToPrevDateRange,
    dateRangeTypeDay,
    dateRangeTypeWeek,
    dateRangeTypeMonth,
} from '../calendar.locstring.json';
import loc, { format } from 'owa-localize';

export default function useExpandableDatePickerViewState({
    selectedDate,
    onSelectDate,
    numDaysInDayView,
    viewType,
    showNavigation,
    onSelectToday,
}: ExpandableDatePickerProps) {
    const today = observableToday();
    const numDays = numDaysInDayView ? Number(numDaysInDayView) : 1;

    const dateRangeRef = React.useRef<HTMLDivElement | null>(null);
    const [dateRangeRect] = useElementSizeTracker(dateRangeRef);
    const navButtonsRef = React.useRef<HTMLDivElement | null>(null);
    const [navButtonsRect] = useElementSizeTracker(navButtonsRef);
    const datePickerRef = React.useRef<HTMLDivElement | null>(null);
    const [datePickerRect] = useElementSizeTracker(datePickerRef);

    const onClickToday = React.useCallback(() => {
        onSelectToday(asDate(today));
    }, [onSelectToday, today]);

    const fluentDateRangeType = getFluentDateRangeType(viewType);
    const workDays = getWorkDays();
    const firstDayOfWeek = getFirstDayOfWeek();
    const showWeekNumbers = getShowWeekNumbers();
    const firstWeekOfYear = getFirstWeekOfYear();

    const owaSelectedDate = convertToOwaDate(selectedDate);

    const dateRange = createDateRangeFromType(
        fluentDateRangeType,
        owaSelectedDate,
        firstDayOfWeek,
        createWorkWeekDaysArray(workDays),
        numDaysInDayView
    );

    const onDateRangeChange = React.useCallback((dateRange: DateRange) => {
        onSelectDate(asDate(dateRange.start));
    }, []);

    const onNextClick = React.useCallback(() => {
        if (viewType == CalendarViewType.Month) {
            onSelectDate(asDate(addMonths(owaSelectedDate, 1)));
        } else {
            onSelectDate(asDate(addDays(owaSelectedDate, numDays)));
        }
    }, [owaSelectedDate, numDays]);

    const onPrevClick = React.useCallback(() => {
        if (viewType == CalendarViewType.Month) {
            onSelectDate(asDate(addMonths(owaSelectedDate, -numDays)));
        } else {
            onSelectDate(asDate(addDays(owaSelectedDate, -numDays)));
        }
    }, [owaSelectedDate, numDays]);

    // always hide 'today' text when asset is below 352px with fallback in case localized
    // daterange/today string is too long to fit all content
    const shouldShowTodayButtonText =
        datePickerRect &&
        dateRangeRect &&
        datePickerRect?.width > TodayButtonTextHideDay &&
        datePickerRect?.width - dateRangeRect?.width > 160;

    // in the event the localized string for date range is too long, we need to se a max width
    // so the text truncated with ellipsis
    const timestripMaxWidth =
        shouldShowTodayButtonText && datePickerRect && navButtonsRect
            ? datePickerRect.width - navButtonsRect.width - 18
            : 'none';

    const dateRangeStripText = formatDateForStrip(dateRange, fluentDateRangeType);

    const prevDateRangeLabel = getPrevDateRangeLabel(viewType, dateRange, numDays);
    const nextDateRangeLabel = getNextDateRangeLabel(viewType, dateRange, numDays);
    const goToTodayButtonLabel = format(loc(goToTodayButtonTooltip), formatMonthDayYear(today));
    return {
        onClickToday: onClickToday,
        fluentDateRangeType: fluentDateRangeType,
        workDays: workDays,
        dateRange: dateRange,
        onDateRangeChange: onDateRangeChange,
        onNextClick: onNextClick,
        onPrevClick: onPrevClick,
        shouldShowTodayButtonText: shouldShowTodayButtonText,
        datePickerRef: datePickerRef,
        dateRangeStripText: dateRangeStripText,
        dateRangeRef: dateRangeRef,
        navButtonsRef: navButtonsRef,
        timestripMaxWidth: timestripMaxWidth,
        prevDateRangeLabel: prevDateRangeLabel,
        nextDateRangeLabel: nextDateRangeLabel,
        showNavigation: showNavigation,
        firstDayOfWeek: firstDayOfWeek,
        showWeekNumbers: showWeekNumbers,
        firstWeekOfYear: firstWeekOfYear,
        goToTodayButtonLabel: goToTodayButtonLabel,
    };
}

function getPrevDateRangeLabel(viewType: CalendarViewType, dateRange: DateRange, numDays: number) {
    const prevDateRange = getNewDateRange(viewType, dateRange, numDays, false /* isNext = false */);
    const dateRangeTypeString = getNewDateRangeString(viewType);
    return format(loc(goToPrevDateRange), dateRangeTypeString, prevDateRange);
}

function getNextDateRangeLabel(viewType: CalendarViewType, dateRange: DateRange, numDays: number) {
    const nextDateRange = getNewDateRange(viewType, dateRange, numDays, true /* isNext = true */);
    const dateRangeTypeString = getNewDateRangeString(viewType);
    return format(loc(goToNextDateRange), dateRangeTypeString, nextDateRange);
}

function getNewDateRange(
    viewType: CalendarViewType,
    dateRange: DateRange,
    numDays: number,
    isNext: boolean
) {
    return isDayGridViewType(viewType) || viewType == CalendarViewType.Agenda
        ? formatMonthDayYear(addDays(dateRange.start, isNext ? numDays : -numDays))
        : formatMonthName(addMonths(dateRange.start, isNext ? 1 : -1));
}

function getNewDateRangeString(viewType: CalendarViewType) {
    return viewType == CalendarViewType.Month
        ? loc(dateRangeTypeMonth)
        : viewType == CalendarViewType.Day || viewType == CalendarViewType.Agenda
        ? loc(dateRangeTypeDay)
        : loc(dateRangeTypeWeek);
}
